import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { prettifyApiError } from "../../shared/api";
import { ErrorBanner } from "../../shared/ui/ErrorBanner";
import { PageHeader } from "../../shared/ui/PageHeader";
import {
  fetchControlli,
  type ControlliFatture,
  type GruppoControllo,
  type RigaControllo,
} from "./fattureFornitoriApi";

const euro = (v: string | null) =>
  v === null ? "—" : Number(v).toLocaleString("it-IT", { style: "currency", currency: "EUR" });

const giorno = (iso: string | null) =>
  iso === null
    ? "—"
    : new Date(`${iso}T12:00:00`).toLocaleDateString("it-IT", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });

const PRIME = 8;

/** Oltre questi giorni senza file nuovi il contatore diventa un avviso. */
const GIORNI_AVVISO = 7;

/** Giorni di calendario fra la data del file piu' recente e oggi. */
export function giorniDa(iso: string, oggi: Date = new Date()): number {
  const da = Date.parse(`${iso}T00:00:00Z`);
  const fino = Date.UTC(oggi.getFullYear(), oggi.getMonth(), oggi.getDate());
  return Math.max(0, Math.round((fino - da) / 86_400_000));
}

const daQuanto = (n: number) => (n === 0 ? "oggi" : n === 1 ? "1 giorno fa" : `${n} giorni fa`);

/** Il promemoria dello scarico da Eureka: quanti giorni ha l'ultima fattura fornitore in OM. */
function Contatore({ fino }: { fino: string | null }) {
  const giorni = fino ? giorniDa(fino) : null;
  const avviso = giorni === null || giorni >= GIORNI_AVVISO;
  return (
    <section
      aria-label="Ultima fattura fornitore"
      className={`mb-5 flex items-baseline gap-3 rounded-lg border px-4 py-3 ${
        avviso ? "border-amber/50 bg-amber-soft" : "border-line bg-card"
      }`}
    >
      <span className="font-display text-[28px] font-bold leading-none text-ink">
        {giorni === null ? "—" : giorni}
      </span>
      <span className="text-[13px] text-ink-2">
        {fino
          ? `giorni dall'ultima fattura fornitore in OM (${giorno(fino)}, ${daQuanto(giorni ?? 0)}).`
          : "Nessuna fattura fornitore in OM."}
        {avviso && " Scarica le fatture nuove da Eureka nella cartella C:\\stampe."}
      </span>
    </section>
  );
}

const PARTI: Array<{ parte: GruppoControllo["parte"]; titolo: string; archivio: string }> = [
  { parte: "fornitori", titolo: "Fornitori", archivio: "fornitori" },
  { parte: "clienti", titolo: "Fatture clienti e SdI", archivio: "sdi" },
];

/** Somma degli importi del gruppo: la differenza se c'e', se no il trovato. */
function inGioco(righe: RigaControllo[]): number | null {
  const valori = righe
    .map((r) => r.differenza ?? r.trovato)
    .filter((v): v is string => v !== null)
    .map(Number);
  return valori.length ? valori.reduce((a, b) => a + Math.abs(b), 0) : null;
}

function Riga({ r }: { r: RigaControllo }) {
  return (
    <tr className="align-top">
      <td className="py-2 pr-3 tabular-nums text-ink-2 max-md:hidden">{giorno(r.data)}</td>
      <td className="py-2 pr-3">
        {r.ordine ? (
          <Link
            to={`/ordini/${encodeURIComponent(r.ordine)}`}
            className="font-semibold text-ink hover:underline"
          >
            {r.ordine}
          </Link>
        ) : (
          <span className="text-ink-2">—</span>
        )}
        <span className="block text-[12px] text-ink-2">
          {r.chi} · {r.documento}
          <span className="md:hidden"> · {giorno(r.data)}</span>
        </span>
        {r.nota && <span className="block text-[12px] text-ink-2">{r.nota}</span>}
      </td>
      <td className="py-2 pr-3 text-right tabular-nums">{euro(r.atteso)}</td>
      <td className="py-2 pr-3 text-right tabular-nums">{euro(r.trovato)}</td>
      <td
        className={`py-2 text-right font-semibold tabular-nums ${
          r.differenza && Number(r.differenza) > 0 ? "text-red" : "text-ink"
        }`}
      >
        {r.differenza === null ? "—" : euro(r.differenza)}
      </td>
    </tr>
  );
}

function Gruppo({ g }: { g: GruppoControllo }) {
  const [tutte, setTutte] = useState(false);
  const n = g.righe.length;
  const somma = inGioco(g.righe);
  const tono =
    n === 0
      ? "bg-green-soft text-ink"
      : g.gravita === "errore"
        ? "bg-red-soft text-ink"
        : "bg-amber-soft text-ink";
  const righe = tutte ? g.righe : g.righe.slice(0, PRIME);
  return (
    <section
      aria-label={g.titolo}
      className="overflow-hidden rounded-lg border border-line bg-card"
    >
      <header className="flex items-start gap-3 px-4 py-3">
        <span
          className={`mt-0.5 shrink-0 rounded-full px-2.5 py-0.5 text-[12.5px] font-bold tabular-nums ${tono}`}
        >
          {n === 0 ? "OK" : n}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold text-ink">{g.titolo}</h3>
          <p className="text-[12.5px] text-ink-2">{g.spiegazione}</p>
        </div>
        {somma !== null && (
          <span className="shrink-0 text-right font-semibold tabular-nums text-ink">
            {euro(String(somma))}
            <span className="block text-[11.5px] font-normal text-ink-2">in gioco</span>
          </span>
        )}
      </header>
      {n > 0 && (
        <div className="overflow-x-auto border-t border-line px-4 pb-2">
          <table className="w-full min-w-[520px] text-left text-[13px]">
            <thead className="text-[12px] text-ink-2">
              <tr>
                <th className="py-1.5 pr-3 font-semibold max-md:hidden">Data</th>
                <th className="py-1.5 pr-3 font-semibold">Ordine e documento</th>
                <th className="py-1.5 pr-3 text-right font-semibold">Atteso</th>
                <th className="py-1.5 pr-3 text-right font-semibold">Fatturato</th>
                <th className="py-1.5 text-right font-semibold">Differenza</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {righe.map((r, i) => (
                <Riga key={`${r.documento}-${r.ordine ?? ""}-${i}`} r={r} />
              ))}
            </tbody>
          </table>
          {n > PRIME && (
            <button
              type="button"
              onClick={() => setTutte((v) => !v)}
              className="my-2 h-[30px] rounded-full border border-line-2 bg-card px-3 text-[12.5px] font-semibold text-ink max-md:h-10"
            >
              {tutte ? "Mostra meno" : `Mostra tutte (${n})`}
            </button>
          )}
        </div>
      )}
    </section>
  );
}

export function ControlliFatturePage() {
  const [dati, setDati] = useState<ControlliFatture | null>(null);
  const [errore, setErrore] = useState<string | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    fetchControlli(ctrl.signal)
      .then(setDati)
      .catch((err: unknown) => {
        if (!ctrl.signal.aborted) setErrore(prettifyApiError(err));
      });
    return () => ctrl.abort();
  }, []);

  return (
    <main className="flex-1 overflow-y-auto px-7 pt-[22px] pb-8 scroll-paper max-md:px-3 max-md:pt-3">
      <PageHeader
        title="Controlli fatture"
        subtitle="Gli errori che le fatture di Eureka fanno vedere: dei fornitori e delle nostre fatture allo SdI. Importi senza IVA per i fornitori, con IVA per i clienti."
        meta={
          <Link to="/fatturazione" className="font-semibold text-ink hover:underline max-md:hidden">
            ← Fatturazione
          </Link>
        }
      />
      <ErrorBanner message={errore} className="mb-3" />
      {dati !== null && <Contatore fino={dati.letti_fino_al.fornitori ?? null} />}
      {dati === null && !errore && (
        <p className="text-[13px] text-ink-2" role="status">
          Controllo le fatture…
        </p>
      )}
      {dati !== null &&
        PARTI.map(({ parte, titolo, archivio }) => {
          const gruppi = dati.gruppi.filter((g) => g.parte === parte);
          const fino = dati.letti_fino_al[archivio] ?? null;
          return (
            <div key={parte} className="mb-6">
              <h2 className="mb-1 font-display text-[18px] font-bold text-ink">{titolo}</h2>
              <p className="mb-3 text-[12.5px] text-ink-2">
                {fino
                  ? `File letti fino al ${giorno(fino)}: lo scarico da Eureka lo fai tu, OM controlla fino a lì.`
                  : "Nessun file ancora: scarica le fatture da Eureka nella cartella C:\\stampe."}
              </p>
              <div className="flex flex-col gap-3">
                {gruppi.map((g) => (
                  <Gruppo key={g.chiave} g={g} />
                ))}
              </div>
            </div>
          );
        })}
    </main>
  );
}
